import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

import request from 'api'
import { putData, useAppDispatch, useAppSelector } from 'store'

type Account = {
  type: 'email' | 'phone'
  value: string
  status: 'NORMAL' | 'SUSPENDED' | 'UNDEFINED'
}
type Address = {
  street: string
  extended: string
  city: string
  state: string
  zipCode: string
}

type Birthday = {
  year: string
  month: string
  day: string
}
type Organization = {
  jobTitle: string
  department: string
  name: string
}
type User = {
  type: '' | 'child'
  status: string
  accounts: Account[]
  phoneNumbers: string[]
  countryCode: string
  languageCode: string
  firstName: string
  nickName: string
  lastName: string
  photoUrl: string
  statusMessage: string
  addresses: Address[]
  birthday: Birthday
  organization: Organization
}

const dummyUser: User = {
  type: '',
  status: '',
  accounts: [],
  phoneNumbers: [],
  countryCode: '',
  languageCode: '',
  firstName: '',
  lastName: '',
  nickName: '',
  photoUrl: '',
  statusMessage: '',
  addresses: [{ street: '', extended: '', city: '', state: '', zipCode: '' }],
  birthday: {
    year: '',
    month: '',
    day: ''
  },
  organization: {
    name: '',
    department: '',
    jobTitle: ''
  }
}

export function useUser(forceFetch = false) {
  const user = useAppSelector((state) => state.data.user as User)
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const fetchUser = async () => {
    const fetchedData = await request
      .get<User>('/v1/users')
      .then((response) => {
        if (response.status !== 200) {
          throw new Error('FailedToFetch')
        }
        return response.data
      })
      .then((data) => {
        const addresses = data.addresses || [
          {
            street: '',
            extended: '',
            city: '',
            state: '',
            zipCode: ''
          }
        ]
        const typeSafeAddresses = addresses.map((address) => {
          return {
            street: address.street || '',
            extended: address.extended || '',
            city: address.city || '',
            state: address.state || '',
            zipCode: address.zipCode || ''
          }
        })

        const user: User = {
          type: data.type || '',
          status: data.status || '',
          accounts: data.accounts || [],
          phoneNumbers: data.phoneNumbers || [],
          countryCode: data.countryCode || '',
          //TODO: data.languageCode is null. To be fixed
          languageCode: data.languageCode || '',
          firstName: data.firstName || '',
          lastName: data.lastName || '',
          nickName: data.nickName || '',
          photoUrl: data.photoUrl || '',
          statusMessage: data.statusMessage || '',
          addresses: typeSafeAddresses,
          birthday: {
            year: data.birthday?.year || '',
            month: data.birthday?.month || '',
            day: data.birthday?.day || ''
          },
          organization: {
            jobTitle: data.organization?.jobTitle || '',
            department: data.organization?.department || '',
            name: data.organization?.name || ''
          }
        }

        let account = user.accounts.filter((account) => account.type === 'email')[0]
        if (!account) {
          account = user.accounts.filter((account) => account.type === 'phone')[0]
        }

        return user
      })
      .catch(() => {
        return dummyUser
      })

    const emailAccounts = fetchedData.accounts.filter((account) => account.type === 'email')
    const phoneAccounts = fetchedData.accounts.filter((account) => account.type === 'phone')

    if (emailAccounts?.length >= 2 || phoneAccounts?.length >= 2) {
      navigate('/error/500')
    }

    dispatch(putData({ user: fetchedData }))
    return fetchedData
  }

  useEffect(() => {
    if (forceFetch || user === undefined) {
      fetchUser()
    }
  }, [])

  return {
    user: user === undefined ? dummyUser : user,
    isInitializing: user === undefined,
    fetchUser
  }
}
